import moment from "moment";
import format from "string-format";
import { extend } from "vee-validate";
import * as rules from "vee-validate/dist/rules";

import {
  ERROR_MSG_EMAIL_FORMAT,
  ERROR_MSG_INVALID_PHONE_NUMBER,
  ERROR_MSG_MAX_CHARACTER,
  ERROR_MSG_MAX_LENGTH,
  ERROR_MSG_MIN_LENGTH,
  ERROR_MSG_REQUIRED,
} from "@/constants/message.constants";
import { BUILD_YEAR_RANGE_MSG } from "@/constants/label.constants";
import numeral from "numeral";

// install rules and localization
Object.keys(rules).forEach((rule) => {
  extend(rule, rules[rule]);
});

extend("required", {
  validate(value) {
    return !!value && (typeof value === "string" ? value.trim() !== "" : true);
  },
  params: ["fieldLabel", "errorMessageTemplate"],
  message: (_, { fieldLabel, errorMessageTemplate }) => {
    if (errorMessageTemplate) {
      return format(errorMessageTemplate, fieldLabel);
    }
    return format(ERROR_MSG_REQUIRED, fieldLabel);
  },
});

extend("email", {
  message: ERROR_MSG_EMAIL_FORMAT,
});

extend("numeric", {
  message: ERROR_MSG_INVALID_PHONE_NUMBER,
});

extend("max", {
  params: ["fieldLabel", "length", "errorMessageTemplate"],
  message: (_, params) => {
    const { fieldLabel, errorMessageTemplate, length } = params;
    if (errorMessageTemplate) {
      return format(errorMessageTemplate, `${fieldLabel}`, `${length}`);
    }
    return format(ERROR_MSG_MAX_LENGTH, `${fieldLabel}`, `${length}`);
  },
});

extend("min_length", {
  validate: (value, { length }) => {
    return value.length >= length;
  },
  params: ["fieldLabel", "length", "errorMessageTemplate"],
  message: (_, params) => {
    const { fieldLabel, errorMessageTemplate, length } = params;
    if (errorMessageTemplate) {
      return format(errorMessageTemplate, `${fieldLabel}`, `${length}`);
    }
    return format(ERROR_MSG_MIN_LENGTH, `${fieldLabel}`, `${length}`);
  },
});

extend("maxLength", {
  validate: (value, { length }) => {
    return value.length <= length;
  },
  params: ["fieldLabel", "length"],
  message: format(ERROR_MSG_MAX_CHARACTER, "{fieldLabel}", "{length}"),
});

extend("max_build_year", {
  validate: (value) => {
    const currentYearPlusOne = new Date().getFullYear() + 2;
    return currentYearPlusOne > value;
  },
  params: ["fieldLabel", "length"],
  message: "Build year must be lower than {length}",
});

extend("min_value", {
  validate: (value, { minValue, inclusive }) => {
    const removeComma = value.split(",").join("");
    const removeCommaMinValue = (minValue + "").split(",").join("");
    const convertStringToNum = parseInt(removeComma);
    return inclusive !== true
      ? convertStringToNum > parseInt(removeCommaMinValue)
      : convertStringToNum >= parseInt(removeCommaMinValue);
  },
  params: ["minValue", "fieldLabel", "inclusive", "customMessage"],
  message: (_, params) => {
    const { fieldLabel, customMessage, minValue } = params;
    if (customMessage) {
      return customMessage;
    }
    return `${fieldLabel} must be greater than £${minValue}.`;
  },
});

extend("max_value", {
  validate: (value, { max }) => {
    const numberValue = numeral(value).value();
    return numberValue <= max;
  },
  params: ["max", "fieldLabel", "customMessage"],
  message: (_, params) => {
    const { fieldLabel, customMessage, max } = params;
    if (customMessage) {
      return customMessage;
    }
    return `${fieldLabel} must be less than £${max}.`;
  },
});

extend("min_currency_value", {
  params: ["minValue", "fieldLabel", "customMessage", "inclusive"],
  validate: (value, { minValue, inclusive }) => {
    return inclusive
      ? numeral(minValue).value() <= numeral(value).value()
      : numeral(minValue).value() < numeral(value).value();
  },
  message: (_, params) => {
    const { fieldLabel, customMessage, minValue } = params;
    if (customMessage) {
      return customMessage;
    }
    return `${fieldLabel} must be greater than £${minValue}.`;
  },
});

extend("max_currency_length", {
  validate: (value, { length }) => {
    return value.replaceAll(",", "").length <= length;
  },
  params: ["fieldLabel", "length", "errorMessageTemplate"],
  message: (_, params) => {
    const { fieldLabel, errorMessageTemplate, length } = params;
    if (errorMessageTemplate) {
      return format(errorMessageTemplate, `${fieldLabel}`, `${length}`);
    }
    return format(ERROR_MSG_MAX_LENGTH, `${fieldLabel}`, `${length}`);
  },
});

extend("min", {
  params: ["fieldLabel", "length"],
  message: format(ERROR_MSG_MIN_LENGTH, "{fieldLabel}", "{length}"),
});

extend("regex", {
  validate: (value, { pattern, flags }) => {
    const regEx = new RegExp(pattern, flags);
    return regEx.test(value);
  },
  params: [
    "fieldLabel",
    "pattern",
    "flags",
    "customMessage",
    "errorMessageTemplate",
  ],
  message: (_, params) => {
    const { fieldLabel, customMessage, errorMessageTemplate } = params;
    if (errorMessageTemplate) {
      return format(errorMessageTemplate, `${fieldLabel}`);
    }
    if (customMessage) {
      return customMessage;
    }
    return `${fieldLabel} format is invalid.`;
  },
});

extend("date_valid", {
  validate: (value, { strict }) => {
    const isValidDate = moment(value, "DD/MM/YYYY", strict).isValid();
    return isValidDate;
  },
  params: ["fieldLabel", "strict", "errorMessageTemplate"],
  message: (_, params) => {
    const { fieldLabel, errorMessageTemplate } = params;
    if (errorMessageTemplate) {
      return format(errorMessageTemplate, fieldLabel);
    }
    return `${fieldLabel} is not a valid date.`;
  },
});

extend("date_in_year_valid", {
  validate: (value, { date }) => {
    if (!value || !date) {
      return true;
    }
    return moment(`${date}`, "DD/MM/YYYY").isValid();
  },
  params: ["fieldLabel", "errorMessageTemplate", "date"],
  message: (_, params) => {
    const { fieldLabel, customMessage } = params;
    if (customMessage) {
      return customMessage;
    }
    return `${fieldLabel} is not a valid date.`;
  },
});

extend("validDateFormat", {
  validate: (value, { format, strict }) => {
    return moment(value, format ?? "DD/MM/YYYY", strict ?? true).isValid();
  },
  params: ["fieldLabel", "format", "strict"],
  message: `{fieldLabel} is not a valid date.`,
});

extend("date_in_range", {
  validate: (value, { min, max, units }) => {
    const userValue = moment(value, "DD/MM/YYYY");
    const minRange = min ? moment().subtract(min, units) : moment(); // now
    const maxRange = moment().add(max, units);

    return moment(userValue).isBetween(
      moment(minRange),
      moment(maxRange),
      "days",
      "[]"
    );
  },
  params: ["fieldLabel", "min", "max", "units", "customMessage"],
  message: (_, params) => {
    const { fieldLabel, customMessage } = params;
    if (customMessage) {
      return customMessage;
    }
    return `${fieldLabel} must start from today and be no more than 1 year in the future.`;
  },
});

extend("date_in_range_past", {
  validate: (value, { max, units }) => {
    const userValue = moment(value, "DD/MM/YYYY");
    const minRange = moment().subtract(max, units);
    const maxRange = moment().subtract(1, "days"); // yesterday - exclude today

    return moment(userValue).isBetween(
      moment(minRange),
      moment(maxRange),
      "days",
      "[]"
    );
  },
  params: ["fieldLabel", "max", "units", "customMessage"],
  message: (_, params) => {
    const { fieldLabel, customMessage } = params;
    if (customMessage) {
      return customMessage;
    }
    return `${fieldLabel} must start from today and be no more than 1 year in the future.`;
  },
});

extend("age_in_range", {
  validate: (value, { min, max }) => {
    const userValue = moment(value, "DD/MM/YYYY");
    const maxRange = moment().subtract(min, "years");
    const minRange = moment().subtract(max, "years");

    return moment(userValue).isBetween(
      moment(minRange),
      moment(maxRange),
      "days",
      "(]"
    );
  },
  params: ["min", "max", "customMessage"],
  message: (_, { min, max, customMessage }) => {
    if (customMessage) {
      return customMessage;
    }
    return format(
      `You must be between {0} and {1} years old to apply for a quotation.`,
      min,
      max
    );
  },
});

extend("between", {
  validate: (value, { min, max }) => {
    value = value.replace(",", ""); //remove ',' for when used for currency i.e with v-mask
    return min <= value && value <= max;
  },
  params: ["fieldLabel", "min", "max"],
  message: `{fieldLabel} must be between {min} and {max}`,
});

extend("propertyYearInRange", {
  validate: (value, { minYear }) => {
    const maxValue = new Date().getFullYear() + 1;
    return minYear <= value && value <= maxValue;
  },
  params: ["minYear", "customMessage"],
  message: (_, params) => {
    const { minYear, customMessage } = params;
    if (customMessage) {
      return customMessage;
    }
    return format(BUILD_YEAR_RANGE_MSG, minYear, new Date().getFullYear() + 1);
  },
});

extend("number_in_range", {
  validate: (value, { min, max }) => {
    const numberValue = numeral(value).value();
    return min <= numberValue && numberValue <= max;
  },
  params: ["fieldLabel", "min", "max"],
  message: `{fieldLabel} must be between {min} and {max}`,
});

extend("between_now_and", {
  validate: (value, { future }) => {
    const userValue = moment(value, "DD/MM/YYYY", true);
    const today = moment().startOf("day");
    const expiryDate = moment(future);

    const isInRange = userValue.isBetween(today, expiryDate, "day", "[]");
    return isInRange;
  },
  params: ["fieldLabel", "future", "customMessage"],
  message: (_, { fieldLabel, customMessage, future }) => {
    if (customMessage) {
      return customMessage;
    }
    return `${fieldLabel} must be between today and ${moment(future).format(
      "DD/MM/YY"
    )}.`;
  },
});

extend("is_duplicate_address_field", {
  validate: (value, { postcode, street, district, town, county }) => {
    if (value === postcode) return false;
    if (value === street) return false;
    if (value === district) return false;
    if (value === town) return false;
    if (value === county) return false;
    return true;
  },
  params: ["postcode", "street", "district", "town", "county"],
  message: "Duplicate values are not allowed.",
});

extend("phone_number", {
  params: ["fieldLabel"],
  validate: (value, { fieldLabel }) => {
    if (!value) {
      return true;
    }

    const numberOnlyPattern = new RegExp("^\\d*$");
    if (!numberOnlyPattern.test(value)) {
      return `Please enter numerical values only.`;
    }

    const startingNumberPattern = new RegExp("^0[1237]");
    if (!startingNumberPattern.test(value)) {
      return `${fieldLabel} must begin with 01, 02, 03 or 07.`;
    }

    const length = value.length;
    if (length < 9) {
      return `${fieldLabel} must be at least 9 characters.`;
    }

    if (length > 15) {
      return `${fieldLabel} may not be greater than 15 characters.`;
    }

    return true;
  },
});

extend("date_is_same_or_after", {
  params: ["compareValue", "durationValue", "customMessage"],
  validate: (value, { compareValue, durationValue }) => {
    if (!compareValue) return true;

    const date = moment(value, "DD/MM/YYYY");

    const compareDate = moment(compareValue, "DD/MM/YYYY");

    if (durationValue) {
      compareDate.add(durationValue);
    }

    return date.isSameOrAfter(compareDate);
  },
  message: (_, params) => {
    const { customMessage } = params;

    return customMessage;
  },
});

extend("date_is_same_or_before", {
  params: ["compareValue", "durationValue", "customMessage"],
  validate: (value, { compareValue, durationValue }) => {
    if (!compareValue) return true;

    const date = moment(value, "DD/MM/YYYY");

    const compareDate = moment(compareValue, "DD/MM/YYYY");

    if (durationValue) {
      compareDate.add(durationValue);
    }

    return date.isSameOrBefore(compareDate);
  },
  message: (_, params) => {
    const { customMessage } = params;

    return customMessage;
  },
});
